import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Activity } from "lucide-react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import AppCard from "../../components/AppCard";
import Button from "../../components/ui/Button";
import SocialAuthButtons from "../../components/SocialAuthButtons";
import { useAuth } from "../../providers/AuthProvider";
import { fieldErrorClassName, inputClassName, labelClassName } from "./signupShared";

interface InvitePreview {
  email: string;
  role: "admin" | "team";
  companyName: string;
  invitedByName: string;
  expiresAt: string;
}

interface FormValues {
  name: string;
  password: string;
  confirmPassword: string;
}

const roleLabel = (role: InvitePreview["role"]) => (role === "admin" ? "Admin" : "Team Member");

const formatExpiry = (expiresAt: string): string => {
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return expiresAt;
  return date.toLocaleString();
};

export default () => {
  const { session, isLoading: isAuthLoading, refreshSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(true);

  const { register, handleSubmit, watch, setError, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: { name: "", password: "", confirmPassword: "" },
  });
  const password = watch("password");

  useEffect(() => {
    if (!token) {
      setLookupError("This invite link is missing a token. Ask your administrator to resend it.");
      setIsLookingUp(false);
      return;
    }

    let cancelled = false;
    setIsLookingUp(true);
    void fetch("/api/auth/invite/stash", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async response => {
        const result = await response.json().catch(() => null) as { invite?: InvitePreview | null; error?: string } | null;
        if (cancelled) return;

        if (!response.ok || !result?.invite) {
          setLookupError(result?.error ?? "This invite link is invalid or has expired.");
          return;
        }
        setInvite(result.invite);
      })
      .catch(() => {
        if (!cancelled) setLookupError("Unable to verify your invite right now. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setIsLookingUp(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (isAuthLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm font-medium text-slate-500">Loading...</div>;
  }

  if (session) {
    if (session.email === invite?.email) {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <ShellWithMessage
        title="You are already signed in"
        body={`This invite was sent to ${invite?.email ?? "another email"}, but you are signed in as ${session.email}. Sign out to accept the invite.`}
        action={<Link to="/api/auth/logout" className="font-medium text-indigo-600 transition hover:text-indigo-500">Sign out</Link>}
      />
    );
  }

  if (isLookingUp) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm font-medium text-slate-500">Verifying invite...</div>;
  }

  if (lookupError || !invite) {
    return (
      <ShellWithMessage
        title="Invite unavailable"
        body={lookupError ?? "This invite link is invalid or has expired."}
        action={<Link to="/user-login" className="font-medium text-indigo-600 transition hover:text-indigo-500">Go to sign in</Link>}
      />
    );
  }

  const onSubmit = handleSubmit(async values => {
    if (values.password !== values.confirmPassword) {
      setError("confirmPassword", { type: "validate", message: "Passwords do not match." });
      return;
    }

    const response = await fetch("/api/auth/password/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: values.name,
        email: invite.email,
        password: values.password,
      }),
    });
    const result = await response.json().catch(() => null) as { error?: string; field?: string } | null;

    if (!response.ok) {
      const field = (result?.field === "name" || result?.field === "password") ? result.field : "password";
      setError(field as keyof FormValues, { type: "server", message: result?.error ?? "Unable to create account." });
      return;
    }

    await refreshSession();
    navigate("/dashboard", { replace: true });
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-slate-900 selection:bg-indigo-100">
      <div className="relative w-full max-w-[480px] space-y-8 py-8">
        <div className="text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-600 shadow-sm">
            <Activity className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Quantira</h1>
          <p className="mt-2 text-xs font-medium text-slate-500">Business Intelligence Platform</p>
        </div>

        <AppCard className="border-slate-200 p-8 shadow-sm">
          <div className="space-y-6">
            <div className="space-y-3 text-center">
              <h2 className="text-lg font-semibold text-slate-900">Accept your invite</h2>
              <p className="text-sm text-slate-500">
                <span className="font-medium text-slate-700">{invite.invitedByName}</span> invited you to join{" "}
                <span className="font-medium text-slate-700">{invite.companyName}</span> as a{" "}
                <span className="font-medium text-slate-700">{roleLabel(invite.role)}</span>.
              </p>
              <p className="text-xs text-slate-400">Expires {formatExpiry(invite.expiresAt)}</p>
            </div>

            <SocialAuthButtons returnTo="/dashboard" disabled={isSubmitting} />

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs font-medium uppercase tracking-wide text-slate-400">
                <span className="bg-white px-3">Or set a password</span>
              </div>
            </div>

            <form className="space-y-4" onSubmit={event => void onSubmit(event)}>
              <div className="space-y-2">
                <label className={labelClassName} htmlFor="invite-email">Email</label>
                <input
                  id="invite-email"
                  type="email"
                  value={invite.email}
                  readOnly
                  className={`${inputClassName} cursor-not-allowed bg-slate-50 text-slate-500`}
                />
              </div>

              <div className="space-y-2">
                <label className={labelClassName} htmlFor="invite-name">Full Name</label>
                <input
                  id="invite-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Enter your full name"
                  className={inputClassName}
                  {...register("name", { required: "Full name is required." })}
                />
                {errors.name && <p className={fieldErrorClassName}>{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <label className={labelClassName} htmlFor="invite-password">Password</label>
                <input
                  id="invite-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Create a password (min. 12 characters)"
                  className={inputClassName}
                  {...register("password", {
                    required: "Password is required.",
                    minLength: { value: 12, message: "Password must be at least 12 characters." },
                  })}
                />
                {errors.password && <p className={fieldErrorClassName}>{errors.password.message}</p>}
              </div>

              <div className="space-y-2">
                <label className={labelClassName} htmlFor="invite-confirm">Confirm Password</label>
                <input
                  id="invite-confirm"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Confirm your password"
                  className={inputClassName}
                  {...register("confirmPassword", {
                    required: "Please confirm your password.",
                    validate: value => value === password || "Passwords do not match.",
                  })}
                />
                {errors.confirmPassword && <p className={fieldErrorClassName}>{errors.confirmPassword.message}</p>}
              </div>

              <Button type="submit" className="h-12 w-full" disabled={isSubmitting}>
                {isSubmitting ? "Accepting invite..." : `Join ${invite.companyName}`}
              </Button>
            </form>
          </div>
        </AppCard>
      </div>
    </div>
  );
};

interface MessageProps {
  title: string;
  body: string;
  action: React.ReactNode;
}

const ShellWithMessage = ({ title, body, action }: MessageProps) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-slate-900 selection:bg-indigo-100">
    <div className="w-full max-w-[440px] space-y-6 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-600 shadow-sm">
        <Activity className="h-8 w-8 text-white" />
      </div>
      <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
      <p className="text-sm text-slate-500">{body}</p>
      <div>{action}</div>
    </div>
  </div>
);
